import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import PropTypes from 'prop-types';
import Loader from 'react-loader-spinner';
import EventsListModalContainer from "../containers/events_list_modal_container";
import ChevronIcon from "./chevron_icon";
import { themedTextClass } from '../utils/css_utils';
import { centerItemInList } from '../utils/scroll_utils'
import { componentText } from '../utils/i18n_utils';

const defaultFormatLabel = (round, roundDates) => roundDates?.[round.id] ? `${round.name} (${roundDates[round.id]})` : round.name;

const previewFailedMessage = () =>
  componentText('rounds_modal.preview_failed', "Couldn't load events for this round. Please try again.");

const RoundsModal = ({
  roundId,
  rounds,
  eventsList,
  roundDates,
  fetchPreviewEvents,
  handleClickEvent,
  handleClickSummary,
  handleClickEmptyRound,
  isManagerSite,
  onRoundChange,
  formatLabel = defaultFormatLabel,
}) => {
  const [previewEvents, setPreviewEvents] = useState(eventsList);
  const [previewError, setPreviewError] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [openRoundId, setOpenRoundId] = useState(roundId);

  useEffect(() => { setOpenRoundId(roundId); }, [roundId]);

  // The sheet caps the list, so the selected round can sit below the fold.
  // Its events render after the first layout and grow it past the viewport,
  // which pushes the aligned row back out of view - so realign while the
  // list is still settling, and stop as soon as the reader scrolls.
  const listRef = useRef(null)
  const selectedRef = useRef(null)
  const readerScrolledRef = useRef(false)

  useLayoutEffect(() => {
    readerScrolledRef.current = false

    const list = listRef.current
    if (!list) {
      return undefined
    }

    // The expanded round is taller than the list, so its header being in view
    // says nothing about the tournament inside it.
    const target = () => list.querySelector('.lbv2__event-btn.is-selected') || selectedRef.current

    centerItemInList(list, target())

    const stopRealigning = () => { readerScrolledRef.current = true }
    list.addEventListener('wheel', stopRealigning, { passive: true })
    list.addEventListener('touchstart', stopRealigning, { passive: true })

    const observer = new ResizeObserver(() => {
      if (!readerScrolledRef.current) {
        centerItemInList(list, target())
      }
    })
    observer.observe(list)
    Array.from(list.children).forEach((child) => observer.observe(child))

    return () => {
      observer.disconnect()
      list.removeEventListener('wheel', stopRealigning)
      list.removeEventListener('touchstart', stopRealigning)
    }
  }, [ roundId ])

  // The highlight always tracks the committed round; browsing previews only
  // moves the open accordion panel. The round changes (and the modal closes)
  // when an event is picked, which updates the roundId prop.
  const isSelected = (round) => String(roundId) === String(round.id);
  const isOpen = (round) => String(openRoundId) === String(round.id);

  async function handleChange(newRoundId) {
    if (onRoundChange) {
      onRoundChange(String(newRoundId));
      return;
    }

    // Accordion toggle: pressing the open round collapses it; opening another
    // round replaces it, so at most one is open.
    if (String(openRoundId) === String(newRoundId)) {
      setOpenRoundId(null);
      return;
    }

    setOpenRoundId(newRoundId);
    setPreviewError(false);
    setPreviewLoading(true);
    const result = await fetchPreviewEvents(newRoundId);

    // 'aborted' means a newer preview fetch superseded this one — that call
    // owns the loading/error state now, so leave everything untouched.
    if (result.status === 'aborted') return;

    setPreviewLoading(false);
    if (result.status === 'ok') {
      setPreviewEvents(result.events);
    } else {
      setPreviewError(true);
    }
  }

  return (
    <ol className="lbv2__rounds-modal" ref={listRef}>
      {rounds.map((round) => (
        <li key={round.id} className="lbv2__round" ref={isSelected(round) ? selectedRef : null}>
          <div className="lbv2__round-name">
            <button
              className={`lbv2__round-btn ${isSelected(round) ? themedTextClass(isManagerSite) : ''}`}
              onClick={() => handleChange(round.id)}
            >
              <div className="lbv2__round-btn-text">
                {roundDates?.[round.id] && onRoundChange ? (
                  <>
                    <div className="lbv2__round-btn-text-name">{formatLabel(round, roundDates)}</div>
                    <div className="lbv2__round-btn-text-date">{roundDates[round.id]}</div>
                  </>
                ) : formatLabel(round, roundDates)}
              </div>
              {!onRoundChange && <ChevronIcon isOpen={isOpen(round)} />}
            </button>
          </div>

          {!onRoundChange && isOpen(round) && (
            previewLoading ? (
              <div className="lbv2__loader">
                <Loader type="Oval" color={ '#333F48' } height={ 20 } width={ 12 }></Loader>
              </div>
            ) : previewError ? (
              <div className="lbv2__error">{previewFailedMessage()}</div>
            ) : (
              <EventsListModalContainer
                selectedRoundId={openRoundId}
                previewEvents={previewEvents}
                handleClickEvent={handleClickEvent}
                handleClickSummary={handleClickSummary}
                handleClickEmptyRound={handleClickEmptyRound}
              />
            )
          )}
        </li>
      ))}
    </ol>
  );
};

RoundsModal.propTypes = {
  roundId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  rounds: PropTypes.array.isRequired,
  eventsList: PropTypes.object,
  roundDates: PropTypes.object,
  fetchPreviewEvents: PropTypes.func,
  handleClickEvent: PropTypes.func,
  handleClickSummary: PropTypes.func,
  handleClickEmptyRound: PropTypes.func,
  isManagerSite: PropTypes.bool,
  onRoundChange: PropTypes.func,
  formatLabel: PropTypes.func,
};

export default RoundsModal;
